import { useQuery } from '@tanstack/react-query'
import { getAssessmentsByShiftId } from 'api/user/assessments'
import {
  AssessmentRequirement,
  Evaluation,
  Interview,
} from 'api/user/assessments/typings'
import { assessmentsKeys } from './keys'

type EvaluationsByWorkerRequirementId = Record<
  number /** Worker Requirement ID */,
  Evaluation
>

type AssessmentStatus = 'pass' | 'fail' | 'needs_evaluation' | 'needs_interview'

type AssessmentsByWorkerId = Record<
  number /** Worker ID */,
  {
    status: AssessmentStatus
    interview: Interview
    evaluations: EvaluationsByWorkerRequirementId
    evaluationNote?: string | null
  }
>

export type UseShiftAssessmentsData = {
  assessmentsByWorkerId: AssessmentsByWorkerId
  requirements: AssessmentRequirement[]
}

const getEvaluationStatus = (
  externalCandidateId,
  evaluations: Evaluation[],
  total: number
): AssessmentStatus => {
  if (!externalCandidateId) {
    return 'needs_interview'
  }

  if (evaluations.some((evaluation) => evaluation.adminStatus === 'fail')) {
    return 'fail'
  }

  if (
    evaluations.length === total &&
    evaluations.every((evaluation) => evaluation.adminStatus === 'pass')
  ) {
    return 'pass'
  }

  return 'needs_evaluation'
}

export const useShiftAssessments = (shiftId: number) => {
  return useQuery({
    placeholderData: {
      assessmentsByWorkerId: {},
      requirements: [],
    },
    initialData: {
      assessmentsByWorkerId: {},
      requirements: [],
    },
    queryKey: assessmentsKeys.byShiftId(shiftId),
    queryFn: () =>
      getAssessmentsByShiftId({ shiftId }).then(
        ({ assessments, workerRequirements }) => {
          const assessmentsByWorkerId = assessments.reduce(
            (
              byWorkerId,
              { workerId, interview, evaluations, evaluationNote }
            ) => {
              const status = getEvaluationStatus(
                interview.externalCandidateId,
                evaluations,
                workerRequirements.length
              )
              byWorkerId[workerId] = {
                status,
                interview,
                evaluationNote,
                evaluations: evaluations.reduce(
                  (byWorkerRequirementId, evaluation) => {
                    byWorkerRequirementId[evaluation.workerRequirementId] =
                      evaluation
                    return byWorkerRequirementId
                  },
                  {} as EvaluationsByWorkerRequirementId
                ),
              }
              return byWorkerId
            },
            {} as AssessmentsByWorkerId
          )

          return {
            assessmentsByWorkerId,
            requirements: workerRequirements,
          } satisfies UseShiftAssessmentsData
        }
      ),
  })
}
